// What a run changed on one project, by reference (design-structured-next-actions-and-
// dates.md 7.4): the summary it recorded, then each specialist's actions and dates with
// the reason it gave, its record edits and its questions, then attributes. Decisions are
// answered in Next actions, where each sits on its action.
// Every item links to where it lives on the specialist's page.
import { Link } from "react-router-dom";

import type {
  RunChanges as RunChangesData,
  RunItemChange,
  RunQuestionChange,
} from "../api/hooks/useStatefulAgents";
import { projectSpecialistPath } from "../routes/learningAgentPaths";
import { actionKind, hardnessLabel, STATUS_LABEL } from "../utils/nextActions";
import { formatDay } from "../utils/attributeFormat";
import { reviewAttrs } from "../utils/reviewState";
import styles from "./RunChanges.module.css";

const VERB: Record<string, string> = {
  create: "New",
  update: "Changed",
  log: "Logged",
  close: "Closed",
  points: "Points changed on",
};

const QUESTION_STATUS: Record<string, string> = {
  open: "waiting on you",
  waiting: "asked back - waiting on you",
  answered: "being applied",
  closed: "closed",
  skipped: "skipped",
};

// A question the run asked or closed, linking to where it is answered.
const QuestionLine = ({
  change,
  projectId,
}: {
  change: RunQuestionChange;
  projectId: string;
}) => (
  <li className={styles.item}>
    <span className={styles.verb}>
      {change.verb === "close" ? "Closed" : "Asked"}
    </span>
    <span>
      <Link
        to={`${projectSpecialistPath(projectId, change.agent)}#${change.anchor}`}
      >
        {change.question}
      </Link>{" "}
      <span className={styles.meta}>
        · {change.kind} · {QUESTION_STATUS[change.status] ?? change.status}
      </span>
      {(change.verb === "close" || change.status === "skipped") &&
        change.closed_note && (
          <span className={styles.reason}>{change.closed_note}</span>
        )}
    </span>
  </li>
);

const ItemLine = ({
  change,
  projectId,
}: {
  change: RunItemChange;
  projectId: string;
}) => {
  const item = change.item;
  const id = change.kind === "action" ? item.action_id : item.date_id;
  const href = `${projectSpecialistPath(projectId, change.agent)}#${id}`;
  const last = item.contact_log?.[item.contact_log.length - 1];
  const facts =
    change.kind === "action"
      ? [
          change.verb === "log" && last
            ? `${last.kind === "sent" ? "sent" : "reply"} ${formatDay(last.at)}`
            : "",
          change.verb === "close" && item.closed
            ? item.closed.outcome
            : change.status
              ? (STATUS_LABEL[change.status.status] ?? change.status.status)
              : "",
          item.due && `due ${formatDay(item.due)}`,
          item.blocking && "blocking",
        ]
      : [
          item.date
            ? formatDay(item.date)
            : (item.state ?? "").replace(/_/g, " "),
          hardnessLabel(item.hardness),
          change.verb === "close" && item.closed ? item.closed.outcome : "",
        ];
  return (
    <li className={styles.item}>
      <span className={styles.verb}>{VERB[change.verb] ?? change.verb}</span>
      <span>
        {change.kind === "action" ? item.title : item.what}
        <span className={styles.meta}>
          {" "}
          · {facts.filter(Boolean).join(" · ")}
        </span>{" "}
        <Link className={styles.suffix} to={href}>
          → {change.kind === "date" ? "date" : actionKind(item.type)}
        </Link>
        {change.reasons.map((reason) => (
          <span key={reason} className={styles.reason}>
            {reason}
          </span>
        ))}
      </span>
    </li>
  );
};

const Summary = ({
  changes,
  projectId,
}: {
  changes: RunChangesData;
  projectId: string;
}) => {
  const summary = changes.summary;
  if (!summary) return null;
  const link = (id: string) => {
    const ref = changes.titles[id];
    return ref ? (
      <Link to={`${projectSpecialistPath(projectId, ref.agent)}#${id}`}>
        {ref.title}
      </Link>
    ) : (
      <span className={styles.meta}>no longer open</span>
    );
  };
  const list = (title: string, ids: string[]) =>
    ids.length > 0 && (
      <div>
        <h5 className={styles.sub}>{title}</h5>
        <ol className={styles.ranked}>
          {ids.map((id) => (
            <li key={id}>{link(id)}</li>
          ))}
        </ol>
      </div>
    );
  return (
    <section className={styles.section}>
      <h4 className={styles.heading}>
        Summary{" "}
        <span className={`${styles.rag} ${styles[summary.rag.verdict]}`}>
          {summary.rag.verdict}
        </span>
      </h4>
      <p>{summary.rag.reason}</p>
      {list("Blockers", summary.blockers)}
      {list("Next steps", summary.next_steps)}
    </section>
  );
};

export const RunChanges = ({
  changes,
  projectId,
  inRun,
}: {
  changes: RunChangesData;
  projectId: string;
  /** Shown in a run's conversation, where text selected in a specialist's part is a
   * comment on its reply; false on the briefing, which is a comment on the briefing. */
  inRun: boolean;
}) => {
  const agents = [
    ...new Set([
      ...changes.items.map((c) => c.agent),
      ...changes.records.map((r) => r.agent),
      ...(changes.questions ?? []).map((q) => q.agent),
    ]),
  ];
  const labelOf = (agent: string) =>
    changes.items.find((c) => c.agent === agent)?.label ??
    changes.records.find((r) => r.agent === agent)?.label ??
    (changes.questions ?? []).find((q) => q.agent === agent)?.label ??
    agent;
  return (
    <div className={styles.changes}>
      <Summary changes={changes} projectId={projectId} />
      {agents.map((agent) => {
        const items = changes.items.filter((c) => c.agent === agent);
        const actions = items.filter((c) => c.kind === "action");
        const dates = items.filter((c) => c.kind === "date");
        const record = changes.records.find((r) => r.agent === agent);
        const questions = (changes.questions ?? []).filter(
          (q) => q.agent === agent,
        );
        return (
          <section
            key={agent}
            className={styles.section}
            {...(inRun
              ? reviewAttrs(
                  "conversation_reply",
                  `${labelOf(agent)}'s changes on this pass`,
                  {
                    agent,
                    doc: `project:${projectId}`,
                  },
                )
              : {})}
          >
            <h4 className={styles.heading}>
              <Link to={projectSpecialistPath(projectId, agent)}>
                {labelOf(agent)}
              </Link>
            </h4>
            {actions.length > 0 && (
              <>
                <h5 className={styles.sub}>Next actions</h5>
                <ul className={styles.list}>
                  {actions.map((c) => (
                    <ItemLine
                      key={c.item.action_id}
                      change={c}
                      projectId={projectId}
                    />
                  ))}
                </ul>
              </>
            )}
            {dates.length > 0 && (
              <>
                <h5 className={styles.sub}>Tracked dates</h5>
                <ul className={styles.list}>
                  {dates.map((c) => (
                    <ItemLine
                      key={c.item.date_id}
                      change={c}
                      projectId={projectId}
                    />
                  ))}
                </ul>
              </>
            )}
            {questions.length > 0 && (
              <>
                <h5 className={styles.sub}>Agent feedback</h5>
                <ul className={styles.list}>
                  {questions.map((q) => (
                    <QuestionLine
                      key={q.question_id}
                      change={q}
                      projectId={projectId}
                    />
                  ))}
                </ul>
              </>
            )}
            {record && (
              <details className={styles.record}>
                <summary>
                  Updated its record: {record.sections.join(", ")}
                </summary>
                <ul className={styles.list}>
                  {record.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        );
      })}
      {changes.attributes.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.heading}>Attributes</h4>
          <ul className={styles.list}>
            {changes.attributes.map((a, i) => (
              <li key={`${a.key}-${i}`} className={styles.item}>
                <span className={styles.verb}>{a.label}</span>
                <span>
                  {a.value}
                  {a.reason && (
                    <span className={styles.reason}>{a.reason}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
